/**
 * OrganisasjonList - Presentational Component
 * Viser liste over organisasjoner med statistikk
 * Følger REACT_PATTERNS.md: Presentational komponent (kun UI rendering)
 */

import React from 'react';
import { Building2, Users, Calendar, Mail, Phone, Edit, Plus, LogIn } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import type { Organisasjon } from '@/services/api';

interface OrganisasjonListProps {
  organisasjoner: Organisasjon[];
  onEdit: (org: Organisasjon) => void;
  onCreate: () => void;
  /** Superadmin går inn i organisasjonen (velger org-kontekst) */
  onGaaInn: (org: Organisasjon) => void;
  loading?: boolean;
}

const OrganisasjonList: React.FC<OrganisasjonListProps> = ({
  organisasjoner,
  onEdit,
  onCreate,
  onGaaInn,
  loading = false,
}) => {
  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center text-muted-foreground">Laster organisasjoner...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-sm">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <CardTitle className="text-2xl font-semibold flex items-center gap-2">
          <Building2 className="h-5 w-5" />
          Organisasjoner
        </CardTitle>
        <Button onClick={onCreate} size="sm">
          <Plus className="h-4 w-4 mr-2" />
          Ny organisasjon
        </Button>
      </CardHeader>
      <CardContent>
        {organisasjoner.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            Ingen organisasjoner funnet. Opprett din første organisasjon.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Navn</TableHead>
                  <TableHead>Kontaktperson</TableHead>
                  <TableHead className="text-center">Brukere</TableHead>
                  <TableHead className="text-center">Produksjoner</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Handlinger</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {organisasjoner.map((org) => (
                  <TableRow key={org.id} className="hover:bg-muted/50">
                    <TableCell className="font-medium">{org.navn}</TableCell>
                    <TableCell>
                      {org.kontaktpersonNavn ? (
                        <div className="space-y-1">
                          <div className="flex items-center gap-1.5 text-sm">
                            <span>{org.kontaktpersonNavn}</span>
                          </div>
                          {org.kontaktpersonEmail && (
                            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                              <Mail className="h-3 w-3" />
                              <span>{org.kontaktpersonEmail}</span>
                            </div>
                          )}
                          {org.kontaktpersonTelefon && (
                            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                              <Phone className="h-3 w-3" />
                              <span>{org.kontaktpersonTelefon}</span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-sm">Ikke satt</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1.5">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        <span>{org.antallBrukere}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1.5">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <span>{org.antallProduksjoner}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={org.isActive ? 'default' : 'secondary'}
                        className={org.isActive ? 'bg-status-confirmed' : 'bg-muted'}
                      >
                        {org.isActive ? 'Aktiv' : 'Inaktiv'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onGaaInn(org)}
                        aria-label={`Velg ${org.navn}`}
                        className="mr-1 h-8 gap-1.5 px-2"
                      >
                        <LogIn className="h-4 w-4" aria-hidden="true" />
                        <span>Velg</span>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onEdit(org)}
                        aria-label="Rediger organisasjon"
                        className="h-8 w-8 p-0"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default OrganisasjonList;

